// src/components/TrackedYouTube.js
import { useRef, useEffect } from 'react';
import YouTube from 'react-youtube';
import amplitude from '../analytics/amplitude';

export default function TrackedYouTube({
  videoId,
  title,
  eventBase = 'Landing Overview', // used in event names
  width = '100%',
  aspect = 16 / 9,
  rounded = true,
}) {
  const playerRef = useRef(null);
  const milestones = useRef(new Set()); // remember sent % markers

  // Map YouTube player states → readable strings
  const YT_PLAYING = 1;
  const YT_ENDED = 0;

  // When the player is ready, keep the instance for later
  const handleReady = e => {
    playerRef.current = e.target;
  };

  const handleStateChange = e => {
    if (!playerRef.current) return;

    const state = e.data;
    const duration = playerRef.current.getDuration();

    if (state === YT_PLAYING) {
      amplitude.track(`${eventBase} – Video Started`, { videoId, title });
    } else if (state === YT_ENDED) {
      amplitude.track(`${eventBase} – Video Completed`, { videoId, title });
    }
  };

  // Fire progress milestones every second
  useEffect(() => {
    let timer;
    const tick = () => {
      if (
        playerRef.current &&
        playerRef.current.getPlayerState() === YT_PLAYING
      ) {
        const current = playerRef.current.getCurrentTime();
        const total = playerRef.current.getDuration();
        if (total) {
          const pct = Math.floor((current / total) * 100);
          [25, 50, 75].forEach(mark => {
            if (pct >= mark && !milestones.current.has(mark)) {
              milestones.current.add(mark);
              amplitude.track(`${eventBase} – ${mark}% Watched`, {
                videoId,
                title,
              });
            }
          });
        }
      }
    };
    timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [eventBase, videoId, title]);

  /* —— presentation —— */
  const paddingTop = `${100 / aspect}%`;
  return (
    <div
      style={{
        position: 'relative',
        paddingTop,
        borderRadius: rounded ? '12px' : 0,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        marginBottom: '1rem',
      }}>
      <YouTube
        videoId={videoId}
        title={title}
        opts={{
          width: '100%',
          height: '100%',
          playerVars: { modestbranding: 1 },
        }}
        onReady={handleReady}
        onStateChange={handleStateChange}
        iframeClassName="yt-iframe"
      />
    </div>
  );
}
